/* eslint-disable no-console */
import { observable, computed, action, reaction } from 'mobx'
import * as Oidc from 'oidc-client'
import { userService } from 'services'
import * as workStationService from '../services/resources/workStation.service'
// import { appInsights } from 'AppInsights'
import ReactGA from 'react-ga'
import posthog from 'posthog-js'
import { trackingId } from 'config/constants'

const config = {
  authority: `${process.env.REACT_APP_OAUTH2_AUTHORITY}`,
  client_id: `${process.env.REACT_APP_OAUTH2_CLIENT_ID}`,
  redirect_uri: `${process.env.REACT_APP_APP_URL}/login-callback`,
  silent_redirect_uri: `${process.env.REACT_APP_APP_URL}/login-silent-callback`,
  response_type: 'code',
  userStore: new Oidc.WebStorageStateStore({ store: window.localStorage }),
  scope: 'offline_access openid profile email phone plantscanner-portal-api plantscanner-api',
  post_logout_redirect_uri: `${process.env.REACT_APP_APP_URL}/logout-callback`,
  // estado do login guardado por até 1 h: quem demora na tela de login (troca de senha, código)
  // não perde o estado e cai em "No matching state found in storage" ao voltar
  staleStateAge: 3600,
  // tolerância para o relógio do computador: a biblioteca recusa o retorno do login quando o
  // horário local difere do servidor em mais de 5 min (padrão), e muitas máquinas de chão de
  // fábrica estão com hora ou fuso errados ("iat is in the future" / "exp is in the past").
  // A validade do token na API continua sendo checada pelo servidor.
  clockSkew: 12 * 60 * 60,
}

// Marca a última vez que o callback do login recomeçou o login sozinho, para não entrar em loop
// (login → callback com erro → login → ...). Fica no sessionStorage: vale só para esta aba.
const CALLBACK_RETRY_KEY = 'oidc-callback-retry-at'
const CALLBACK_RETRY_WINDOW_MS = 2 * 60 * 1000

const readCallbackRetry = () => {
  try {
    return Number(window.sessionStorage.getItem(CALLBACK_RETRY_KEY)) || 0
  } catch (error) {
    return 0
  }
}

const writeCallbackRetry = (value) => {
  try {
    if (value) window.sessionStorage.setItem(CALLBACK_RETRY_KEY, String(value))
    else window.sessionStorage.removeItem(CALLBACK_RETRY_KEY)
  } catch (error) {
    // sessionStorage indisponível (modo privado/bloqueado): segue sem a trava
  }
}

export default class AuthStore {
  @observable initialized = false
  @observable retry = false
  @observable inProgress = false
  @observable errors = undefined
  @observable userManager = undefined
  @observable user = undefined
  @observable email = undefined
  @observable roles = []
  @observable currentUserId = undefined
  @observable accessToken = undefined
  @observable selectedTenant = undefined
  @observable availableTenants = undefined
  @observable getUserRolesInProcess = false
  @observable tenantFeatures = undefined
  @observable workStations = []
  @observable workStationsLoading = false
  @observable userAllowedWorkStations = []

  constructor(rootStore) {
    this.rootStore = rootStore

    Oidc.Log.logger = console
    Oidc.Log.level = Oidc.Log.WARN

    console.debug('OIDC - authStore was initialized')

    this.userManager = new Oidc.UserManager(config)
    this.userManager.clearStaleState()
    reaction(
      () => this.getUserRolesInProcess,
      (value) => {
        if (value) this.setUserRoles()
      }
    )
  }

  @computed
  get isLoggedIn() {
    return !!this.accessToken
  }

  @computed
  get hasRestrictedRole() {
    const restrictedRoles = ['admin', 'superuser', 'manufacturing_eng']
    return this.roles.some((role) => restrictedRoles.includes(role))
  }

  @computed
  get filteredWorkStations() {
    // If user has restricted role, show all workstations
    if (this.hasRestrictedRole) {
      return this.workStations
    }

    // If user doesn't have restricted role, filter based on allowed workstations
    if (this.userAllowedWorkStations && this.userAllowedWorkStations.length > 0) {
      const result = this.workStations.filter((ws) => this.userAllowedWorkStations.includes(ws.id))

      return result
    }

    // If no allowed workstations defined, return empty array for non-restricted users
    return []
  }

  @computed
  get canAccessAllWorkStations() {
    return this.hasRestrictedRole
  }

  get accessTokenExpired() {
    // renova um minuto antes de vencer, para a requisição não chegar com o token já vencido na API
    if (!this.user) return true
    return this.user.expired || (typeof this.user.expires_in === 'number' && this.user.expires_in < 60)
  }

  @action setGetUserRolesInProcess() {
    this.getUserRolesInProcess = true
  }

  @action async initialize() {
    console.debug('OIDC - initialize was called')
    const user = await this.userManager.getUser()
    console.debug('OIDC - initialize has user in userManager', user)
    if (user) await this.updateUser(user)
    else if (this.retry <= 3)
      return setTimeout(() => {
        this.retry++
        this.initialize()
      }, 1000)

    this.retry = 0
    this.initialized = true
    this.setGetUserRolesInProcess()
    console.debug('OIDC - initialize was finished')
  }

  @action async setUserRoles() {
    console.debug('OIDC - setUserRoles was called')
    if (this.isLoggedIn) {
      const currentUser = await userService.getCurrent()
      this.currentUserId = currentUser?.id
      this.roles = (await userService.getUserRolesByEmail(this.email)).map((ro) => ro.name)
      console.debug(`OIDC - setUserRoles was called - isLoggedIn ${this.isLoggedIn}`)
      console.debug('OIDC - setUserRoles was called - roles', this.roles)

      // Load workstations after roles are set
      await this.loadWorkStations()

    }
    this.getUserRolesInProcess = false
  }

  @action async loadWorkStations() {
    console.debug('OIDC - loadWorkStations was called')
    this.workStationsLoading = true

    try {
      const [workStationsResponse, allowedWorkStationsResponse] = await Promise.all([
        workStationService.getAll(),
        userService.getCurrentUserAllowedWorkStations(),
      ])

      this.workStations = workStationsResponse
      this.userAllowedWorkStations = allowedWorkStationsResponse || []

      console.debug('OIDC - loadWorkStations loaded', workStationsResponse.length, 'workstations')
      console.debug(
        'OIDC - loadWorkStations loaded',
        this.userAllowedWorkStations.length,
        'allowed workstations'
      )
    } catch (error) {
      console.error('OIDC - loadWorkStations error:', error)
      this.workStations = []
      this.userAllowedWorkStations = []
    } finally {
      this.workStationsLoading = false
    }
  }

  @action async login(tenant, { resetCallbackRetry = false } = {}) {
    this.inProgress = true
    this.errors = undefined
    if (resetCallbackRetry) writeCallbackRetry(0)
    console.debug(`OIDC - Login was walled with ${tenant}`)
    if (tenant && tenant !== this.selectedTenant) {
      const newConfig = {
        ...config,
        acr_values: `tenant:${tenant}`,
      }

      this.userManager = new Oidc.UserManager(newConfig)
    }
    return await this.userManager.signinRedirect()
  }

  @action async completeLogin() {
    this.inProgress = true
    this.errors = undefined

    let user
    try {
      user = await this.userManager.signinRedirectCallback()
    } catch (error) {
      // Callback repetido (voltar/atualizar a página, login em duas abas) ou estado expirado:
      // "No matching state found in storage". Se já há sessão válida, segue com ela; senão,
      // recomeça o login em vez de deixar o usuário parado nesta tela.
      const message = (error && error.message) || 'Falha ao concluir o login'
      console.warn('OIDC - signinRedirectCallback failed', message)
      const existing = await this.userManager.getUser().catch(() => null)
      if (existing && !existing.expired) {
        writeCallbackRetry(0)
        await this.updateUser(existing)
        this.inProgress = false
        return
      }
      // Recomeça o login uma vez só. Se o callback falhar de novo logo em seguida, o erro não
      // se resolve sozinho (ex.: relógio do computador errado): para e mostra a mensagem, em
      // vez de ficar indo e voltando da tela de login.
      const lastRetry = readCallbackRetry()
      if (!lastRetry || Date.now() - lastRetry > CALLBACK_RETRY_WINDOW_MS) {
        writeCallbackRetry(Date.now())
        await this.userManager.signinRedirect()
        return
      }
      this.errors = message
      this.inProgress = false
      return
    }

    writeCallbackRetry(0)

    console.debug('OIDC - completeLogin was walled with', user)
    if (user) {
      await this.updateUser(user)
      this.inProgress = false
    } else await this.userManager.signinRedirect()
  }

  @action async loginSilent(tenant) {
    this.inProgress = true
    this.errors = undefined

    if (tenant && tenant !== this.selectedTenant) {
      const newConfig = {
        ...config,
        acr_values: `tenant:${tenant}`,
      }

      this.userManager = new Oidc.UserManager(newConfig)
    }
    await this.userManager.signinSilent()
  }

  @action async completeLoginSilent() {
    this.inProgress = true
    this.errors = undefined

    const user = await this.userManager.signinSilentCallback()
    if (user) {
      this.inProgress = false
      await this.updateUser(user)
    }
  }

  refreshAccessTokenPromise = undefined
  @action refreshAccessToken() {
    console.debug('OIDC - refreshAccessToken was walled')

    if (this.refreshAccessTokenPromise) {
      console.debug('OIDC - refreshAccessTokenPromise in progress')
      return this.refreshAccessTokenPromise
    } else {
      this.refreshAccessTokenPromise = (async () => {
        try {
          const user = await this.userManager.signinSilent()
          await this.updateUser(user)
        } catch (err) {
          if (err.message === 'invalid_grant') await this.clearUser()
        } finally {
          this.refreshAccessTokenPromise = undefined
        }
      })()

      return this.refreshAccessTokenPromise
    }
  }

  async updateUser(user) {
    console.debug('OIDC - updateUser was called with', user)
    if (!user) return
    await this.userManager.storeUser(user)
    // appInsights.setAuthenticatedUserContext(
    //   `${user.profile['sub']}-${user.profile['selected-tenant']}`,
    //   user.profile['email'],
    //   true
    // )
    this.user = user
    this.email = user.profile['email']
    this.selectedTenant = user.profile['selected-tenant']
    this.availableTenants = user.profile['available-tenants']
    this.accessToken = user.access_token
    this.tenantFeatures = user.profile['selected-tenant-features']
      ? JSON.parse(user.profile['selected-tenant-features'])
      : undefined

    localStorage.setItem('user-email', user.profile['email'])
    localStorage.setItem('user-tenant', user.profile['selected-tenant'])

    ReactGA.initialize(trackingId, { debug: true })
    ReactGA.set({ userId: user.profile['email'] })

    // PostHog: liga erros e navegação ao usuário e ao cliente (tenant)
    try {
      const tenant = user.profile['selected-tenant']
      posthog.identify(user.profile['email'], { email: user.profile['email'], tenant })
      if (tenant) {
        posthog.register({ tenant })
        posthog.group('tenant', tenant)
      }
    } catch (error) {
      console.warn('PostHog identify', error)
    }
  }

  async clearUser() {
    try {
      posthog.reset()
    } catch (error) {
      console.warn('PostHog reset', error)
    }
    console.debug('OIDC - clearUser was called with in store', this.user)
    // appInsights.clearAuthenticatedUserContext()
    await this.userManager.removeUser()
    this.user = undefined
    this.accessToken = undefined
    this.email = undefined
    this.currentUserId = undefined
    this.roles = []
    this.selectedTenant = undefined
    this.availableTenants = undefined
  }

  @action
  async selectTenant(tenant) {
    await this.refreshAccessToken()
    this.login(tenant).then(() => this.clearUser())
  }

  @action
  logout() {
    this.inProgress = true
    this.userManager.signoutRedirect().catch((error) => {
      console.error(error)
    })
  }

  @action
  completeLogout() {
    console.debug('OIDC - completeLogout was called')
    this.inProgress = true
    this.userManager
      .signoutRedirectCallback()
      .then(() => {
        this.userManager.removeUser()
      })
      .then(() => {
        this.user = null
        this.selectedTenant = undefined
        this.availableTenants = undefined
        this.inProgress = false
      })
      .catch((error) => console.error(error))
  }
}
